import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Eye, ShieldCheck, Upload, Loader2, KeyRound, AlertTriangle, Send } from "lucide-react";
import { api } from "@/lib/api";
import { aErr, Btn, CopyBtn, Label, inputCls } from "@/components/argus/ui";
import { ArgusLegalFooter } from "@/pages/ArgusLegal";

const SAFETY = [
  "Use a personal phone or computer, never a work device.",
  "Avoid work Wi-Fi or VPN. Mobile data or a public network is safer.",
  "Don't include your name, job title or anything only you would know, unless you want to.",
  "Send copies or photos of documents, never originals.",
  "For the highest anonymity, open this page in the Tor Browser.",
];

function ThreadCheck() {
  const [code, setCode] = useState(""); const [thread, setThread] = useState(null); const [msg, setMsg] = useState(""); const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false); const [err, setErr] = useState(""); const [fileKey, setFileKey] = useState(0);
  const view = async () => {
    setBusy(true); setErr("");
    try { const { data } = await api.post("/argus/public/tip-thread/view", { code }); setThread(data); }
    catch (e) { setErr(aErr(e, "Code not found")); setThread(null); } finally { setBusy(false); }
  };
  const reply = async () => {
    setBusy(true); setErr("");
    const fd = new FormData(); fd.append("code", code); fd.append("message", msg); [...files].forEach(f => fd.append("files", f));
    try { await api.post("/argus/public/tip-thread/reply", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 300000 }); setMsg(""); setFiles([]); setFileKey(k => k + 1); await view(); }
    catch (e) { setErr(aErr(e, "Could not send")); } finally { setBusy(false); }
  };
  return (
    <div className="p-5 rounded-xl argus-glass" data-testid="tip-thread-check">
      <div className="flex items-center gap-2"><KeyRound className="w-4 h-4 text-[#10b981]" /><Label>Already sent a tip? Check for replies</Label></div>
      <div className="flex gap-2 mt-3"><input className={`${inputCls} font-mono uppercase`} value={code} onChange={e => setCode(e.target.value)} placeholder="YOUR-ACCESS-CODE" data-testid="tip-code-input" /><Btn busy={busy && !thread} onClick={view} disabled={code.length < 8} data-testid="tip-code-view">Open</Btn></div>
      {err && <div className="text-red-300 text-xs mt-2" data-testid="tip-thread-err">{err}</div>}
      {thread && (
        <div className="mt-4 space-y-2" data-testid="tip-thread">
          <div className="text-xs text-white/50">Status: <span className="text-white uppercase font-mono">{thread.status}</span></div>
          {thread.messages.map((m, i) => (
            <div key={i} className={`p-3 rounded-lg text-sm whitespace-pre-wrap ${m.from === "team" ? "bg-[#10b981]/10 border border-[#10b981]/30 ml-6" : "bg-black/30 border border-white/10 mr-6"}`} data-testid={`tip-thread-msg-${i}`}>
              <div className="text-[10px] font-mono uppercase text-white/40 mb-1">{m.from === "team" ? "Investigations team" : "You"} · {new Date(m.at).toLocaleString()}{m.files ? ` · ${m.files} file(s)` : ""}</div>{m.text}
            </div>
          ))}
          {thread.status !== "closed" && (
            <div className="pt-2">
              <textarea className={`${inputCls} min-h-[80px]`} value={msg} onChange={e => setMsg(e.target.value)} placeholder="Reply or add more detail…" data-testid="tip-reply-input" />
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <input key={fileKey} type="file" multiple accept=".pdf,.docx,.jpg,.jpeg,.png,.webp,.txt,.csv" onChange={e => setFiles(e.target.files)} className="text-xs text-white/60" data-testid="tip-reply-files" />
                <Btn busy={busy} onClick={reply} disabled={msg.trim().length < 1} data-testid="tip-reply-send"><Send className="w-3.5 h-3.5" /> Send</Btn>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ArgusTip() {
  const { slug } = useParams();
  const [info, setInfo] = useState(null); const [missing, setMissing] = useState(false);
  const [subject, setSubject] = useState(""); const [message, setMessage] = useState(""); const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false); const [pct, setPct] = useState(0); const [err, setErr] = useState(""); const [code, setCode] = useState("");

  useEffect(() => {
    document.title = "Submit a confidential tip";
    const metas = [["robots", "noindex, nofollow"], ["referrer", "no-referrer"]].map(([n, c]) => { const m = document.createElement("meta"); m.name = n; m.content = c; document.head.appendChild(m); return m; });
    api.get(`/argus/public/tip/${slug}`).then(r => setInfo(r.data)).catch(() => setMissing(true));
    return () => metas.forEach(m => m.remove());
  }, [slug]);

  const submit = async () => {
    if ([...files].length > 5) { setErr("Up to 5 files per message"); return; }
    setBusy(true); setErr("");
    const fd = new FormData(); fd.append("subject", subject); fd.append("message", message); [...files].forEach(f => fd.append("files", f));
    try {
      const { data } = await api.post(`/argus/public/tip/${slug}`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 300000, onUploadProgress: e => setPct(Math.round((e.loaded / (e.total || 1)) * 100)) });
      setCode(data.code);
    } catch (e) { setErr(aErr(e, "Could not send your tip")); } finally { setBusy(false); }
  };

  return (
    <main className="min-h-screen argus-root argus-bg text-[#f0fdf4] px-5 py-10" data-testid="tip-page">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2 text-[#10b981]"><Eye className="w-5 h-5" /><Label>Confidential tip line · powered by Argus</Label></div>
        {missing ? (
          <div data-testid="tip-missing">
            <h1 className="argus-display text-3xl font-bold mt-4">This tip line isn't open right now.</h1>
            <p className="text-white/60 mt-2 text-sm">Please check the link with the organisation that shared it. If you run this tip line, sign in to Argus and turn it on in the tip inbox.</p>
          </div>
        ) : !info ? <Loader2 className="w-5 h-5 animate-spin mt-6" /> : code ? (
          <div className="mt-6 p-6 rounded-xl border-2 border-[#10b981] bg-[#10b981]/5" data-testid="tip-success">
            <ShieldCheck className="w-8 h-8 text-emerald-400" />
            <h1 className="argus-display text-3xl font-bold mt-3">Your tip was sent securely.</h1>
            <p className="text-white/70 mt-2 text-sm">Save this access code now. It is the <b>only</b> way to read replies or add more information, and it can't be recovered.</p>
            <div className="mt-4 flex items-center gap-3 flex-wrap"><code className="text-2xl font-mono font-bold tracking-wider text-[#10b981]" data-testid="tip-code">{code}</code><CopyBtn text={code} testId="tip-code-copy" /></div>
            <p className="text-xs text-white/40 mt-4">Check back in a few days using "Check for replies" below.</p>
          </div>
        ) : (
          <>
            <h1 className="argus-display text-4xl sm:text-5xl font-bold mt-3 leading-tight">Tell {info.org_name} what's happening, safely.</h1>
            {info.intro && <p className="text-white/70 mt-3">{info.intro}</p>}
            <div className="mt-6 p-4 rounded-lg bg-emerald-400/5 border border-emerald-400/30" data-testid="tip-safety">
              <div className="flex items-center gap-2 text-emerald-200 text-sm font-semibold"><AlertTriangle className="w-4 h-4" /> Before you submit</div>
              <ul className="mt-2 space-y-1 text-sm text-white/70">{SAFETY.map(s => <li key={s}>• {s}</li>)}</ul>
              <p className="text-[11px] text-white/45 mt-3">No account or name is needed. We don't store your IP address with your tip, and hidden details in photos, PDFs and Word files (author, GPS location, device) are removed automatically. No web form is completely untraceable, so follow the steps above.</p>
            </div>
            <div className="mt-6 space-y-3">
              <input className={inputCls} value={subject} onChange={e => setSubject(e.target.value)} placeholder="Short subject (optional)" maxLength={200} data-testid="tip-subject" />
              <textarea className={`${inputCls} min-h-[180px]`} value={message} onChange={e => setMessage(e.target.value)} placeholder="What happened? Who was involved, when and where? How do you know? Where could evidence be found?" data-testid="tip-message" />
              <label className="flex items-center gap-2 border border-dashed border-white/20 rounded-md p-4 text-sm text-white/60 cursor-pointer hover:border-[#10b981]/60" data-testid="tip-files-label">
                <Upload className="w-4 h-4" /> {files.length ? `${files.length} file(s) selected` : "Attach evidence (up to 5 files, 20 MB each: PDF, DOCX, JPG, PNG, TXT, CSV)"}
                <input type="file" multiple accept=".pdf,.docx,.jpg,.jpeg,.png,.webp,.txt,.csv" className="hidden" onChange={e => setFiles(e.target.files)} data-testid="tip-files" />
              </label>
              {err && <div className="text-red-300 text-sm" data-testid="tip-err">{err}</div>}
              <Btn busy={busy} onClick={submit} disabled={message.trim().length < 20} className="!px-5 !py-2.5 !text-sm" data-testid="tip-submit"><ShieldCheck className="w-4 h-4" /> {busy ? `Sending… ${pct}%` : "Send tip securely"}</Btn>
            </div>
          </>
        )}
        {!missing && <div className="mt-10"><ThreadCheck /></div>}
      </div>
      <ArgusLegalFooter />
    </main>
  );
}
